import { ReviewStar } from "../../../../assets/icons/ReviewStar";
import { TrustpilotLogo } from "../../../../assets/icons/TrustpilotLogo";
import { Verified } from "../../../../assets/icons/Verified";
import clsx from "clsx";
import type { TrustpilotDisplayReview } from "@made-people/grease-trustpilot";
import { useTranslate } from "@made-people/grease-translations";
import { useLocale } from "../../../../hooks/use-locale";
import { TRUSTPILOT_CONSTANTS } from "../trustpilot-constants";

export const StarRating = ({
  rating,
  className,
  starClassName,
}: {
  rating: number;
  className?: string;
  starClassName?: string;
}) => {
  const getStarFillPercentage = (starPosition: number): number => {
    const diff = rating - (starPosition - 1);
    if (diff >= 1) return 100;
    if (diff <= 0) return 0;
    return diff * 100;
  };

  return (
    <div className={clsx("flex", className)}>
      {[1, 2, 3, 4, 5].map((star) => (
        <ReviewStar
          key={star}
          fillPercentage={getStarFillPercentage(star)}
          className={starClassName}
        />
      ))}
    </div>
  );
};

const formatDate = (dateString: string, locale?: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale || "sv-SE");
  } catch {
    return dateString;
  }
};

export const ReviewCard = ({
  review,
  className,
  truncateText = false,
}: {
  review: TrustpilotDisplayReview;
  className?: string;
  truncateText?: boolean;
}) => {
  const translate = useTranslate();
  const locale = useLocale();

  return (
    <div
      className={clsx(
        "bg-white rounded-md p-4 flex flex-col text-black min-h-[220px]",
        className,
      )}
    >
      <StarRating
        rating={review.rating}
        className="text-black mb-4"
        starClassName="h-5 w-5"
      />

      <div className="flex-1 mb-4">
        {review.title && <h3 className="font-semibold mb-2">{review.title}</h3>}
        {review.text && (
          <p className={clsx("text-sm", truncateText && "line-clamp-4")}>
            {review.text}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-sm text-text-less-emphasis-1 justify-end">
        <Verified className="h-4 w-4" />
        <span>{review.author}</span>
      </div>

      <div className="flex items-center justify-between text-sm text-text-less-emphasis-1 mt-2 flex-wrap">
        <div className="flex gap-0.5 text-sm items-center justify-start">
          <span className="text-text-less-emphasis-1 whitespace-nowrap">
            {translate("Trustpilot_Collected_Via")}
          </span>

          {review.source === null ? (
            <span>Miss Mary</span>
          ) : (
            <a
              href={TRUSTPILOT_CONSTANTS.URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {" "}
              <TrustpilotLogo className="h-6 w-fit" />
            </a>
          )}
        </div>
        <span>{formatDate(review.date, locale)}</span>
      </div>
    </div>
  );
};
